// src/hooks/useKeycloakAuth.js - Improved Version
import { useKeycloak } from "@react-keycloak/web";
import { useEffect, useState } from "react";

const ROLES_KEY = "userRoles";
const USERNAME_KEY = "username";

const useKeycloakAuth = () => {
  const { keycloak, initialized } = useKeycloak();

  const [username, setUsername] = useState("User");
  const [roles, setRoles] = useState([]);

  const isLogin = !!keycloak?.authenticated;
  const isLoading = !initialized;

  useEffect(() => {
    if (keycloak.authenticated) {
      const isFirstLogin = !sessionStorage.getItem('hasLoggedIn');
      if (isFirstLogin) {
        sessionStorage.setItem('hasLoggedIn', 'true');
        window.location.replace('/landing-page');
      }
    }
  }, [keycloak.authenticated]);

  // Compute username & roles from tokenParsed (runs on every render/change)
  useEffect(() => {
    if (isLogin && keycloak?.tokenParsed) {
      const parsed = keycloak.tokenParsed;

      // Extract username
      const newUsername =
        parsed.preferred_username ||
        (parsed.email?.split("@")[0]) ||
        parsed.sub ||
        "User";
      setUsername(newUsername);

      // Extract roles (client + realm, unique)
      const clientRoles = parsed.resource_access?.Redoms?.roles || []; // Replace 'Redoms' if your client ID differs
      const realmRoles = parsed.realm_access?.roles || [];
      const newRoles = [...new Set([...clientRoles, ...realmRoles])];
      setRoles(newRoles);

      // Sync to sessionStorage (for RequireRole/Sidebar that read directly from storage)
      sessionStorage.setItem(USERNAME_KEY, newUsername);
      sessionStorage.setItem(ROLES_KEY, JSON.stringify(newRoles));
    } else {
      // Cleanup on logout/not authenticated
      setUsername("User");
      setRoles([]);
      sessionStorage.removeItem(USERNAME_KEY);
      sessionStorage.removeItem(ROLES_KEY);
    }
  }, [keycloak?.tokenParsed, isLogin]);

  // === MANUAL TOKEN REFRESH (Critical when checkLoginIframe: false) ===
  useEffect(() => {
    if (!keycloak || !initialized || !isLogin) return;

    // Proactively refresh token if it will expire in <30 seconds
    const refreshInterval = setInterval(() => {
      keycloak
        .updateToken(30)
        .then((refreshed) => {
          if (refreshed) {
            console.debug("Token refreshed successfully");
            // tokenParsed is automatically updated by keycloak-js
          } else {
            console.debug("Token still valid (no refresh needed)");
          }
        })
        .catch((err) => {
          console.error("Failed to refresh token:", err);
          logout(); 
        });
    }, 60000); 

    // Initial refresh attempt on mount
    keycloak.updateToken(30).catch(() => logout());

    return () => clearInterval(refreshInterval);
  }, [keycloak, initialized, isLogin]);

  // Helper functions
  const hasRole = (role) => roles.includes(role);
  const hasAnyRole = (roleList) => roleList.some((r) => roles.includes(r));

  const logout = () => {
    sessionStorage.removeItem(USERNAME_KEY);
    sessionStorage.removeItem(ROLES_KEY);
    sessionStorage.removeItem('hasLoggedIn');
    keycloak?.logout({ redirectUri: window.location.origin });
  };

  return {
    isLogin,
    isLoading,
    username,
    roles,
    hasRole,
    hasAnyRole,
    logout,
    keycloak, // Expose for advanced use (e.g., manual updateToken elsewhere)
  };
};

export default useKeycloakAuth;